import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, Search, DollarSign, Users, Baby, Calendar,
  ArrowRightLeft, Snowflake, Sun, GraduationCap, Ban
} from 'lucide-react';

import DaycareChildrenModal from './modals/DaycareChildrenModal';
import DaycareEnrollmentsModal from './modals/DaycareEnrollmentsModal';
import DaycareBillingModal from './modals/DaycareBillingModal';
import DaycarePackagesModal from './modals/DaycarePackagesModal';
import DaycareChildTransactionHistoryModal from './modals/DaycareChildTransactionHistoryModal';
import DaycareTransferPackageModal from './modals/DaycareTransferPackageModal';
import DaycareFreezeModal from './modals/DaycareFreezeModal';
import DaycareUnfreezeModal from './modals/DaycareUnfreezeModal';
import DaycareCancelModal from './modals/DaycareCancelModal';
import DaycareAdditionalChargesModal from './modals/DaycareAdditionalChargesModal';
import DaycarePrintReportModal from './modals/DaycarePrintReportModal';
import DaycareDailyReport from './DaycareDailyReport';
import DaycareAttendance from './DaycareAttendance';
import AgeWarningsAlert from './AgeWarningsAlert';
import { Gate, useAccessStudio } from './AccessStudio';

// Shared style for the compact icon actions in the enrollments table.
const daycareIconBtn = 'p-1.5 rounded hover:bg-gray-100 transition-colors';

// Tab visibility -> required view permission. Reports tabs share reports.view.
const DAYCARE_TAB_PERMISSIONS = {
  packages: 'daycare.packages.view',
  children: 'daycare.children.view',
  enrollments: 'daycare.enrollments.view',
  billing: 'daycare.billing.view',
  attendance: 'daycare.attendance.view',
  'daily-report': 'daycare.daily_report.view',
  balances: 'daycare.reports.view',
  'monthly-report': 'daycare.reports.view',
  'children-report': 'daycare.reports.view',
};

const Daycare = ({ apiCall, selectedYear: initialYear, availableYears = [], hasRole, showSuccess, showError }) => {
  // Daycare is continuous and runs on its OWN year, independent of the global
  // switcher AND of the school's current year — daycare enrollments don't
  // auto-roll when the school year flips, so its data may still live in an
  // earlier year. Defaults to the active year but the user switches it here.
  // Daycare is monthly and ignores the school academic year: it defaults to
  // "All Years" so EVERY currently-active child shows, regardless of which year
  // their enrollment was tagged with (a 25-26 child still attending stays
  // visible even after a 26-27 child registers). Pick a specific year only for
  // per-year financial reports.
  const [selectedYear, setSelectedYear] = useState('all');

  // A NEW enrollment must be tagged to a concrete year: the package lookup
  // (sp_GetDaycarePackagesForChild) filters by AcademicYear and returns nothing
  // for the "all" filter — which reads as "no suitable packages" for every
  // child. So when the screen is on All Years, fall back to the school's current
  // year (then the latest available year) for enrolling.
  const enrollmentYear =
    (selectedYear && selectedYear !== 'all') ? selectedYear
    : (initialYear && initialYear !== 'all') ? initialYear
    : (availableYears[availableYears.length - 1] || availableYears[0] || '');

  // Helper function to safely format currency
  const safePrice = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '0.00';
    return Number(value).toFixed(2);
  };

  // Data states
  const [packages, setPackages] = useState([]);
  const [children, setChildren] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI states
  const [activeTab, setActiveTab] = useState('packages'); // packages, children, enrollments, billing
  const { userHasPermission } = useAccessStudio();
  // If the user can't see the current tab, land them on the first tab they can.
  useEffect(() => {
    const order = ['packages', 'children', 'enrollments', 'billing', 'attendance', 'daily-report', 'balances', 'monthly-report', 'children-report'];
    const canSee = (tab) => {
      const [m, f, a] = DAYCARE_TAB_PERMISSIONS[tab].split('.');
      return userHasPermission(m, f, a);
    };
    if (!canSee(activeTab)) {
      const first = order.find(canSee);
      if (first) setActiveTab(first);
    }
  }, [userHasPermission, activeTab]);
  const [showChildModal, setShowChildModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [showUnfreezeModal, setShowUnfreezeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAdditionalChargesModal, setShowAdditionalChargesModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  // Move-to-school popup: optionally link the daycare child to a school student
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveEnrollment, setMoveEnrollment] = useState(null);
  const [moveStudents, setMoveStudents] = useState([]);
  const [moveStudentId, setMoveStudentId] = useState('');
  const [moveSubmitting, setMoveSubmitting] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [balanceReport, setBalanceReport] = useState([]);
  const [monthlyReport, setMonthlyReport] = useState([]); // ✅ NEW
  const [showZeroBalances, setShowZeroBalances] = useState(false); // ✅ NEW
  const [childrenDetailedReport, setChildrenDetailedReport] = useState([]); // ✅ NEW

  // Stats
  const [stats, setStats] = useState({
    totalPackages: 0,
    totalCapacity: 0,
    totalEnrolled: 0,
    activeFamilies: 0,
    monthlyRevenue: 0,
    totalBalance: 0
  });

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [packagesData, childrenData, enrollmentsData, familiesData, summaryData] = await Promise.all([
        apiCall(`/daycare/packages?academicYear=${selectedYear}`),
        apiCall('/daycare/children'),
        apiCall(`/daycare/enrollments?academicYear=${selectedYear}`),
        apiCall('/families'),
        apiCall(`/daycare/outstanding-summary?academicYear=${selectedYear}`).catch(() => null)
      ]);

      setPackages(packagesData);
      setChildren(childrenData);
      setEnrollments(enrollmentsData);
      setFamilies(familiesData);

      // Calculate stats
      const totalCapacity = packagesData.reduce((sum, pkg) => sum + pkg.MaxCapacity, 0);
      const totalEnrolled = packagesData.reduce((sum, pkg) => sum + pkg.CurrentEnrollment, 0);
      const activeEnrollments = enrollmentsData.filter(e => e.Status === 'Active');
      const activeFamilies = new Set(activeEnrollments.map(e => e.FamilyID)).size;
      const monthlyRevenue = activeEnrollments.reduce((sum, e) => sum + (e.FinalMonthlyRate || 0), 0);
      // Outstanding comes from the charge ledger, NOT enrollment.BalanceRemaining
      // (a legacy column the ledger never updates — it summed to -68,029.99
      // against +22,039.01 actually owed). The summary also picks up family-level
      // "old balance" rows, which belong to no enrollment.
      const totalBalance = summaryData
        ? Number(summaryData.Outstanding) || 0
        : enrollmentsData.reduce((sum, e) => sum + (e.TrueBalance || 0), 0);

      setStats({
        totalPackages: packagesData.length,
        totalCapacity,
        totalEnrolled,
        activeFamilies,
        monthlyRevenue,
        totalBalance
      });
    } catch (error) {
      showError('Error loading daycare data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, selectedYear, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load balance report
  const loadBalanceReport = useCallback(async () => {
    try {
      const report = await apiCall(`/daycare/balance-report-detailed?academicYear=${selectedYear}`);
      setBalanceReport(report);
    } catch (error) {
      console.error('Error loading balance report:', error);
    }
  }, [apiCall, selectedYear]);

  useEffect(() => {
    if (activeTab === 'balances') {
      loadBalanceReport();
    }
  }, [activeTab, loadBalanceReport]);

  // ✅ NEW: Load monthly report
  const loadMonthlyReport = useCallback(async () => {
    try {
      const report = await apiCall(`/daycare/monthly-report?academicYear=${selectedYear}`);
      setMonthlyReport(report);
    } catch (error) {
      console.error('Error loading monthly report:', error);
      showError('Error loading monthly report: ' + error.message);
    }
  }, [apiCall, selectedYear, showError]);

  useEffect(() => {
    if (activeTab === 'monthly-report') {
      loadMonthlyReport();
    }
  }, [activeTab, loadMonthlyReport]);

  // ✅ NEW: Load children detailed report
  const loadChildrenDetailedReport = useCallback(async () => {
    try {
      const report = await apiCall(`/daycare/children-detailed-report?academicYear=${selectedYear}`);
      setChildrenDetailedReport(report);
    } catch (error) {
      console.error('Error loading children detailed report:', error);
      showError('Error loading children detailed report: ' + error.message);
    }
  }, [apiCall, selectedYear, showError]);

  useEffect(() => {
    if (activeTab === 'children-report') {
      loadChildrenDetailedReport();
    }
  }, [activeTab, loadChildrenDetailedReport]);

  // ✅ NEW: Filter balance report
  const filteredBalanceReport = useMemo(() => {
    if (showZeroBalances) {
      // Show ALL families (including zero balances)
      return balanceReport;
    }
    // Show only families with outstanding balances
    return balanceReport.filter(family => {
      const totalBalance = family.TotalBalance || family.Balance || 0;
      return totalBalance > 0;
    });
  }, [balanceReport, showZeroBalances]);

  // Grouped packages by age group
  const groupedPackages = useMemo(() => {
    return packages.reduce((groups, pkg) => {
      const group = pkg.AgeGroupName;
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(pkg);
      return groups;
    }, {});
  }, [packages]);

  // Filtered children
  const filteredChildren = useMemo(() => {
    if (!searchTerm) return children;
    const searchLower = searchTerm.toLowerCase();
    return children.filter(child =>
      child.FirstName.toLowerCase().includes(searchLower) ||
      child.LastName.toLowerCase().includes(searchLower) ||
      child.FamilyID.toLowerCase().includes(searchLower)
    );
  }, [children, searchTerm]);

  // Open the Move-to-School popup. Loads the family's existing school students
  // so the daycare child can optionally be linked to one (for "from daycare"
  // history on the family statement). Linking is optional — if left blank, the
  // backend still auto-links a single unambiguous same-name match.
  const handleMoveToSchool = async (enrollment) => {
    setMoveEnrollment(enrollment);
    setMoveStudentId('');
    setMoveStudents([]);
    setShowMoveModal(true);
    try {
      const students = await apiCall(`/students?familyId=${encodeURIComponent(enrollment.FamilyID)}`);
      setMoveStudents(Array.isArray(students) ? students : []);
    } catch (_) {
      setMoveStudents([]); // non-fatal — they can still move without linking
    }
  };

  const submitMoveToSchool = async () => {
    if (!moveEnrollment) return;
    setMoveSubmitting(true);
    try {
      const result = await apiCall(`/daycare/enrollments/${moveEnrollment.EnrollmentID}/move-to-school`, {
        method: 'PUT',
        body: JSON.stringify({
          endDate: new Date().toISOString().split('T')[0],
          reason: 'Student enrolled at school',
          studentId: moveStudentId || undefined
        })
      });
      const linked = result && result.linkedStudentId;
      showSuccess(linked
        ? `Student moved to school and linked to ${linked}`
        : 'Student moved to school successfully');
      setShowMoveModal(false);
      setMoveEnrollment(null);
      loadData();
    } catch (error) {
      showError('Error moving student to school: ' + error.message);
    } finally {
      setMoveSubmitting(false);
    }
  };

  // Helper functions
  const formatTime = (time) => {
    if (!time) return '';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let months = (today.getFullYear() - birthDate.getFullYear()) * 12;
    months += today.getMonth() - birthDate.getMonth();
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years > 0) {
      return `${years}y ${remainingMonths}m`;
    }
    return `${months}m`;
  };

  const getAvailabilityColor = (available) => {
    if (available > 5) return 'bg-green-100 text-green-800';
    if (available > 0) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getAgeGroupColor = (ageMonths) => {
    if (ageMonths <= 8) return 'bg-blue-100 text-blue-800';
    if (ageMonths <= 12) return 'bg-purple-100 text-purple-800';
    return 'bg-pink-100 text-pink-800';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Frozen':
        return 'bg-blue-100 text-blue-800';
      case 'Cancelled':
        return 'bg-gray-100 text-gray-800';
      case 'Enrolled at School': // ✅ NEW
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading daycare data...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-start gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Daycare Management</h1>
          <p className="text-gray-600 mt-1">Monthly program — all active children shown regardless of academic year</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Filter</span>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Years</option>
            {[...new Set([initialYear, ...availableYears].filter((y) => y && y !== 'all'))].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Age Warnings Alert */}
      <AgeWarningsAlert
        academicYear={selectedYear}
        apiCall={apiCall}
        showSuccess={showSuccess}
        showError={showError}
      />

      {/* Stats Cards — management overview (packages, revenue, balances). Hidden
          from teachers, who only hold daycare.daily_report.* */}
      <Gate permission="daycare.reports.view" label="Daycare stats">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Packages</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalPackages}</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Capacity</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCapacity}</p>
            </div>
            <Users className="w-8 h-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Enrolled</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalEnrolled}</p>
            </div>
            <Baby className="w-8 h-8 text-pink-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Families</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeFamilies}</p>
            </div>
            <Users className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Monthly Revenue</p>
              <p className="text-2xl font-bold text-green-600">${safePrice(stats.monthlyRevenue)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Balance</p>
              <p className={`text-2xl font-bold ${stats.totalBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                ${safePrice(stats.totalBalance)}
              </p>
            </div>
            <DollarSign className={`w-8 h-8 ${stats.totalBalance > 0 ? 'text-red-500' : 'text-green-500'}`} />
          </div>
        </div>
      </div>
      </Gate>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {['packages', 'children', 'enrollments', 'billing', 'attendance', 'daily-report', 'balances', 'monthly-report', 'children-report'].map((tab) => (
              <Gate key={tab} permission={DAYCARE_TAB_PERMISSIONS[tab]} label={`${tab} tab`}>
              <button
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'monthly-report' ? '📊 Monthly Report' :
                 tab === 'children-report' ? '👶 Children Report' :
                 tab === 'daily-report' ? '📔 Daily Report' :
                 tab === 'attendance' ? '✅ Attendance' : tab}
              </button>
              </Gate>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* PACKAGES TAB */}
          {activeTab === 'packages' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Daycare Packages</h2>
                <Gate permission="daycare.packages.create" label="Create Package">
                  <button
                    onClick={() => setShowPackageModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                    Create Package
                  </button>
                </Gate>
              </div>

              {Object.keys(groupedPackages).sort().map((ageGroup) => (
                <div key={ageGroup} className="mb-8">
                  <h3 className="text-lg font-semibold bg-gray-100 px-4 py-2 rounded mb-4">
                    {ageGroup}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time Slot</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schedule</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days/Week</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monthly Rate</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meals</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Capacity</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Available</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ratio</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {groupedPackages[ageGroup].map((pkg) => {
                          const available = pkg.MaxCapacity - pkg.CurrentEnrollment;
                          return (
                            <tr key={pkg.PackageID}>
                              <td className="px-4 py-3 whitespace-nowrap font-medium">{pkg.TimeSlot}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {formatTime(pkg.StartTime)} - {formatTime(pkg.EndTime)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm">{pkg.DaysPerWeek} days</td>
                              <td className="px-4 py-3 whitespace-nowrap font-semibold text-blue-600">
                                ${safePrice(pkg.MonthlyRate)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm">
                                <div className="flex flex-wrap gap-1">
                                  {pkg.IncludesBreakfast && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Breakfast</span>}
                                  {pkg.IncludesLunch && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Lunch</span>}
                                  {pkg.IncludesSnacks && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Snacks</span>}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm">{pkg.MaxCapacity}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded ${getAvailabilityColor(available)}`}>
                                  {available} spots
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm">{pkg.TeacherToChildRatio}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CHILDREN TAB */}
          {activeTab === 'children' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Daycare Children</h2>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search children..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <Gate permission="daycare.children.create" label="Register Child">
                    <button
                      onClick={() => setShowChildModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      Register Child
                    </button>
                  </Gate>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Child ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Family</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DOB</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Age</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Age Group</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gender</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Allergies</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Emergency</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredChildren.map((child) => (
                      <tr key={child.ChildID} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{child.ChildID}</td>
                        <td className="px-4 py-3 whitespace-nowrap font-medium">
                          <button
                            onClick={() => {
                              setSelectedChild(child);
                              setShowTransactionModal(true);
                            }}
                            className="text-purple-600 hover:text-purple-800 hover:underline transition-colors"
                          >
                            {child.FirstName} {child.LastName}
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{child.FamilyID}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {new Date(child.DateOfBirth).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {calculateAge(child.DateOfBirth)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${getAgeGroupColor(child.AgeInMonths)}`}>
                            {child.CurrentAgeGroup || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{child.Gender}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {child.Allergies ? (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">Has Allergies</span>
                          ) : (
                            <span className="text-gray-400 text-sm">None</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {child.EmergencyContact}<br />
                          <span className="text-gray-500 text-xs">{child.EmergencyPhone}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {child.EnrollmentStatus === 'Active' ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">Enrolled</span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded">Not Enrolled</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredChildren.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No children found
                </div>
              )}
            </div>
          )}

          {/* ENROLLMENTS TAB */}
          {activeTab === 'enrollments' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Active Enrollments</h2>
                <Gate permission="daycare.enrollments.create" label="New Enrollment">
                  <button
                    onClick={() => setShowEnrollModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                    New Enrollment
                  </button>
                </Gate>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Child</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Family</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Package</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Charged</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-36">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {enrollments.map((enrollment) => (
                      <tr key={enrollment.EnrollmentID} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {enrollment.FirstName} {enrollment.LastName}
                            {enrollment.Status !== 'Active' && (
                              <span
                                className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${getStatusColor(enrollment.Status)}`}
                                title={enrollment.Status === 'Frozen' ? enrollment.FreezeReason || 'Frozen' : enrollment.Status}
                              >
                                {enrollment.Status === 'Frozen' && '🧊 '}
                                {enrollment.Status === 'Cancelled' && '❌ '}
                                {enrollment.Status === 'Enrolled at School' && '🏫 '}
                                {enrollment.Status}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            #{enrollment.EnrollmentID} · from {new Date(enrollment.StartDate).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-gray-800 truncate max-w-[12rem]">
                            {enrollment.FatherName || enrollment.MotherName || '—'}
                          </div>
                          <div className="text-xs text-gray-500">{enrollment.FamilyID}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-gray-800 truncate max-w-[14rem]" title={enrollment.PackageName}>
                            {enrollment.PackageName}
                          </div>
                          <div className="text-xs text-gray-500">{enrollment.TimeSlot}</div>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <div className="font-semibold text-blue-600">
                            ${safePrice(enrollment.FinalMonthlyRate)}
                          </div>
                          {(enrollment.DiscountAmount && enrollment.DiscountAmount > 0) ? (
                            <div className="text-xs text-green-600">
                              -${safePrice(enrollment.DiscountAmount)} off ${safePrice(enrollment.MonthlyRate)}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <div>${safePrice(enrollment.ChargedToDate || 0)}</div>
                          <div className="text-xs text-gray-500">
                            {enrollment.ChargeCount || 0} charge{(enrollment.ChargeCount || 0) !== 1 ? 's' : ''}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {/* TrueBalance = charged - paid off MonthlyCharges.
                              BalanceRemaining is a stale legacy column and reads
                              deeply negative (rate minus cumulative payments). */}
                          <span className={`font-semibold ${
                            (enrollment.TrueBalance || 0) > 0
                              ? 'text-red-600'
                              : 'text-green-600'
                          }`}>
                            ${safePrice(enrollment.TrueBalance || 0)}
                          </span>
                          {(enrollment.PaidToDate || 0) > 0 && (
                            <div className="text-xs text-gray-500">
                              ${safePrice(enrollment.PaidToDate)} paid
                            </div>
                          )}
                        </td>
                        {/* Icon buttons: five actions in the width two labels
                            used to need, so nothing is pushed off-screen.
                            Every icon carries a title for hover discovery. */}
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-0.5">
                            <button
                              onClick={() => {
                                setSelectedEnrollment(enrollment);
                                setShowAdditionalChargesModal(true);
                              }}
                              className={daycareIconBtn + ' text-purple-600 hover:text-purple-800'}
                              title="Charges — registration, books, extras"
                            >
                              <DollarSign className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedEnrollment(enrollment);
                                setShowTransferModal(true);
                              }}
                              className={daycareIconBtn + ' text-blue-600 hover:text-blue-800'}
                              title="Transfer package"
                            >
                              <ArrowRightLeft className="w-4 h-4" />
                            </button>
                            {enrollment.Status === 'Active' ? (
                              <button
                                onClick={() => {
                                  setSelectedEnrollment(enrollment);
                                  setShowFreezeModal(true);
                                }}
                                className={daycareIconBtn + ' text-orange-600 hover:text-orange-800'}
                                title="Freeze enrollment"
                              >
                                <Snowflake className="w-4 h-4" />
                              </button>
                            ) : enrollment.Status === 'Frozen' ? (
                              <button
                                onClick={() => {
                                  setSelectedEnrollment(enrollment);
                                  setShowUnfreezeModal(true);
                                }}
                                className={daycareIconBtn + ' text-green-600 hover:text-green-800'}
                                title="Unfreeze enrollment"
                              >
                                <Sun className="w-4 h-4" />
                              </button>
                            ) : null}
                            {enrollment.Status === 'Active' && (
                              <button
                                onClick={() => handleMoveToSchool(enrollment)}
                                className={daycareIconBtn + ' text-indigo-600 hover:text-indigo-800'}
                                title="Move to school — ends the daycare enrollment"
                              >
                                <GraduationCap className="w-4 h-4" />
                              </button>
                            )}
                            {(enrollment.Status === 'Active' || enrollment.Status === 'Frozen') && (
                              <button
                                onClick={() => {
                                  setSelectedEnrollment(enrollment);
                                  setShowCancelModal(true);
                                }}
                                className={daycareIconBtn + ' text-red-600 hover:text-red-800'}
                                title="Cancel enrollment"
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {enrollments.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No active enrollments for {selectedYear}
                </div>
              )}
            </div>
          )}

          {/* BILLING TAB */}
          {activeTab === 'billing' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Monthly Billing</h2>
                <Gate permission="daycare.billing.process" label="Process Monthly Charges">
                  <button
                    onClick={() => setShowBillingModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <DollarSign className="w-4 h-4" />
                    Process Monthly Charges
                  </button>
                </Gate>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">Billing Workflow</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                  <li>Click "Process Monthly Charges" to generate charges for all active enrollments</li>
                  <li>Charges are created in MonthlyCharges table</li>
                  <li>Go to Payment module to apply charges to family balances</li>
                  <li>Families can then pay through the Payment module</li>
                </ol>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="text-sm text-gray-600 mb-1">Active Enrollments</div>
                  <div className="text-3xl font-bold text-gray-900">{enrollments.length}</div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="text-sm text-gray-600 mb-1">Active Families</div>
                  <div className="text-3xl font-bold text-gray-900">{stats.activeFamilies}</div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="text-sm text-gray-600 mb-1">Expected Monthly Revenue</div>
                  <div className="text-3xl font-bold text-green-600">${safePrice(stats.monthlyRevenue)}</div>
                </div>
              </div>
            </div>
          )}

          {/* BALANCE REPORT TAB */}
          {activeTab === 'attendance' && (
            <DaycareAttendance apiCall={apiCall} showSuccess={showSuccess} showError={showError} />
          )}

          {activeTab === 'daily-report' && (
            <DaycareDailyReport apiCall={apiCall} showSuccess={showSuccess} showError={showError} />
          )}

          {activeTab === 'balances' && (
            <div>
              <div className="mb-6 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold mb-2">Family Balance Report</h2>
                  <p className="text-gray-600 text-sm">Shows all families with outstanding balances (current + old balance)</p>
                  {/* ✅ NEW: Checkbox to show zero balances */}
                  <div className="mt-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={showZeroBalances}
                        onChange={(e) => setShowZeroBalances(e.target.checked)}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Show families with $0 balance</span>
                    </label>
                  </div>
                </div>
                
                {/* Print Button */}
                <button
                  onClick={() => {
                    const printWindow = window.open('', '', 'width=900,height=700');
                    const printContent = document.getElementById('balance-report-print').innerHTML;
                    
                    printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                        <head>
                          <title>Daycare Balance Report - ${selectedYear}</title>
                          <style>
                            * {
                              margin: 0;
                              padding: 0;
                              box-sizing: border-box;
                            }
                            
                            body { 
                              font-family: 'Arial', sans-serif;
                              padding: 20px;
                              background: white;
                            }
                            
                            .header {
                              text-align: center;
                              border-bottom: 3px solid #1e40af;
                              padding-bottom: 15px;
                              margin-bottom: 25px;
                            }
                            
                            .header h1 {
                              font-size: 28px;
                              font-weight: bold;
                              color: #1e3a8a;
                              margin-bottom: 5px;
                            }
                            
                            .header .subtitle {
                              font-size: 16px;
                              color: #4b5563;
                              font-weight: 600;
                            }
                            
                            .header .date-info {
                              font-size: 12px;
                              color: #6b7280;
                              margin-top: 8px;
                            }
                            
                            .summary-section {
                              display: grid;
                              grid-template-columns: repeat(4, 1fr);
                              gap: 15px;
                              margin: 25px 0;
                              page-break-inside: avoid;
                            }
                            
                            .summary-card {
                              border: 2px solid #e5e7eb;
                              border-radius: 8px;
                              padding: 12px;
                              text-align: center;
                            }
                            
                            .summary-card.blue { border-color: #3b82f6; background: #eff6ff; }
                            .summary-card.green { border-color: #10b981; background: #f0fdf4; }
                            .summary-card.red { border-color: #ef4444; background: #fef2f2; }
                            .summary-card.orange { border-color: #f97316; background: #fff7ed; }
                            
                            .summary-label {
                              font-size: 11px;
                              color: #6b7280;
                              font-weight: 600;
                              text-transform: uppercase;
                              margin-bottom: 5px;
                            }
                            
                            .summary-value {
                              font-size: 22px;
                              font-weight: bold;
                            }
                            
                            .summary-card.blue .summary-value { color: #1e40af; }
                            .summary-card.green .summary-value { color: #047857; }
                            .summary-card.red .summary-value { color: #dc2626; }
                            .summary-card.orange .summary-value { color: #ea580c; }
                            
                            table {
                              width: 100%;
                              border-collapse: collapse;
                              margin-top: 20px;
                              page-break-inside: auto;
                            }
                            
                            thead {
                              background: #f3f4f6;
                            }
                            
                            th {
                              padding: 10px 8px;
                              text-align: left;
                              font-size: 11px;
                              font-weight: 700;
                              color: #374151;
                              text-transform: uppercase;
                              border: 1px solid #d1d5db;
                            }
                            
                            td {
                              padding: 8px;
                              font-size: 12px;
                              border: 1px solid #e5e7eb;
                              color: #1f2937;
                            }
                            
                            tbody tr:nth-child(even) {
                              background: #f9fafb;
                            }
                            
                            .text-right { text-align: right; }
                            .text-center { text-align: center; }
                            
                            .status-badge {
                              display: inline-block;
                              padding: 3px 8px;
                              border-radius: 12px;
                              font-size: 10px;
                              font-weight: 600;
                            }
                            
                            .status-active {
                              background: #d1fae5;
                              color: #065f46;
                              border: 1px solid #10b981;
                            }
                            
                            .status-inactive {
                              background: #fee2e2;
                              color: #991b1b;
                              border: 1px solid #ef4444;
                            }
                            
                            .amount-current { color: #dc2626; font-weight: 600; }
                            .amount-old { color: #ea580c; font-weight: 600; }
                            .amount-total { color: #991b1b; font-weight: 700; }
                            
                            tfoot {
                              background: #f9fafb;
                              border-top: 3px solid #1e40af;
                            }
                            
                            tfoot td {
                              font-weight: 700;
                              font-size: 13px;
                              padding: 10px 8px;
                            }
                            
                            .footer {
                              margin-top: 40px;
                              padding-top: 15px;
                              border-top: 2px solid #e5e7eb;
                              text-align: center;
                              font-size: 11px;
                              color: #6b7280;
                            }
                            
                            .footer .school-name {
                              font-weight: 700;
                              color: #1e40af;
                              margin-bottom: 3px;
                            }
                            
                            @media print {
                              body { padding: 15px; }
                              thead { display: table-header-group; }
                              tfoot { display: table-footer-group; }
                              tr { page-break-inside: avoid; }
                            }
                          </style>
                        </head>
                        <body>
                          ${printContent}
                        </body>
                      </html>
                    `);
                    
                    printWindow.document.close();
                    printWindow.print();
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print Report
                </button>
              </div>

              {/* Printable Content */}
              <div id="balance-report-print">
                {/* Header */}
                <div className="header">
                  <h1>Collège des Apôtres</h1>
                  <div className="subtitle">Daycare Balance Report</div>
                  <div className="date-info">
                    Academic Year: {selectedYear} | Generated: {new Date().toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 summary-section">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border-2 border-blue-200 summary-card blue">
                    <div className="text-blue-600 text-sm font-semibold mb-1 summary-label">Total Families</div>
                    <div className="text-3xl font-bold text-blue-900 summary-value">
                      {balanceReport.length}
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border-2 border-green-200 summary-card green">
                    <div className="text-green-600 text-sm font-semibold mb-1 summary-label">Active Families</div>
                    <div className="text-3xl font-bold text-green-900 summary-value">
                      {balanceReport.filter(f => f.HasActiveEnrollment).length}
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-lg border-2 border-red-200 summary-card red">
                    <div className="text-red-600 text-sm font-semibold mb-1 summary-label">Total Outstanding</div>
                    <div className="text-3xl font-bold text-red-900 summary-value">
                      ${filteredBalanceReport.reduce((sum, f) => sum + (f.TotalBalance || 0), 0).toFixed(2)}
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border-2 border-orange-200 summary-card orange">
                    <div className="text-orange-600 text-sm font-semibold mb-1 summary-label">Old Balance</div>
                    <div className="text-3xl font-bold text-orange-900 summary-value">
                      ${filteredBalanceReport.reduce((sum, f) => sum + (f.OldBalance || 0), 0).toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Balance Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Family ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Family Name
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Current Balance
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Old Balance
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Outstanding
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {balanceReport.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                            No families with outstanding balances
                          </td>
                        </tr>
                      ) : (
                        filteredBalanceReport.map((family) => (
                          <tr key={family.FamilyID} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {family.FamilyID}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {family.FamilyName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                              <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(family.EnrollmentStatus || 'Cancelled')}`}>
                                {family.EnrollmentStatus === 'Active' && '✅ '}
                                {family.EnrollmentStatus === 'Frozen' && '🧊 '}
                                {family.EnrollmentStatus === 'Cancelled' && '❌ '}
                                {family.EnrollmentStatus === 'Enrolled at School' && '🏫 '}
                                {family.EnrollmentStatus || 'Cancelled'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                              <span className={family.CurrentBalance > 0 ? 'text-red-600 font-semibold' : 'text-gray-900'}>
                                ${safePrice(family.CurrentBalance)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                              <span className={family.OldBalance > 0 ? 'text-orange-600 font-semibold' : 'text-gray-900'}>
                                ${safePrice(family.OldBalance)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                              <span className="font-bold text-red-700">
                                ${safePrice(family.TotalBalance)}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="3" className="px-6 py-4 text-sm font-bold text-gray-900">
                          TOTAL
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-red-600">
                          ${filteredBalanceReport.reduce((sum, f) => sum + (f.CurrentBalance || 0), 0).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-orange-600">
                          ${filteredBalanceReport.reduce((sum, f) => sum + (f.OldBalance || 0), 0).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-red-700">
                          ${filteredBalanceReport.reduce((sum, f) => sum + (f.TotalBalance || 0), 0).toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Footer */}
                <div className="footer">
                  <div className="school-name">Collège des Apôtres</div>
                  <div>Jounieh, Lebanon | Tel: +961 5 924 845</div>
                  <div style={{ marginTop: '5px', fontSize: '10px' }}>
                    This report was generated electronically and is valid without signature
                  </div>
                </div>
              </div>

              {/* Export Button */}
              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => {
                    const csv = [
                      ['Family ID', 'Family Name', 'Status', 'Current Balance', 'Old Balance', 'Total Outstanding'],
                      ...filteredBalanceReport.map(f => [
                        f.FamilyID,
                        f.FamilyName,
                        f.EnrollmentStatus || 'Cancelled',
                        f.CurrentBalance.toFixed(2),
                        f.OldBalance.toFixed(2),
                        f.TotalBalance.toFixed(2)
                      ]),
                      ['', '', 'TOTAL', 
                        filteredBalanceReport.reduce((sum, f) => sum + (f.CurrentBalance || 0), 0).toFixed(2),
                        filteredBalanceReport.reduce((sum, f) => sum + (f.OldBalance || 0), 0).toFixed(2),
                        filteredBalanceReport.reduce((sum, f) => sum + (f.TotalBalance || 0), 0).toFixed(2)
                      ]
                    ].map(row => row.join(',')).join('\n');
                    
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `daycare-balance-report-${selectedYear}.csv`;
                    a.click();
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Export to CSV
                </button>
              </div>
            </div>
          )}

          {/* ✅ NEW: MONTHLY REPORT TAB */}
          {activeTab === 'monthly-report' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Monthly Income Report</h2>
                <button
                  onClick={() => setShowPrintModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  📄 Print Report
                </button>
              </div>

              {monthlyReport.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No monthly data available for {selectedYear}
                </div>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
                      <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                      <p className="text-2xl font-bold text-blue-600">
                        ${monthlyReport.reduce((sum, m) => sum + (m.TotalRevenue || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
                      <p className="text-sm text-gray-600 mb-1">Total Discounts</p>
                      <p className="text-2xl font-bold text-orange-600">
                        ${monthlyReport.reduce((sum, m) => sum + (m.TotalDiscounts || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
                      <p className="text-sm text-gray-600 mb-1">Net Revenue</p>
                      <p className="text-2xl font-bold text-green-600">
                        ${monthlyReport.reduce((sum, m) => sum + (m.NetRevenue || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
                      <p className="text-sm text-gray-600 mb-1">Collected</p>
                      <p className="text-2xl font-bold text-purple-600">
                        ${monthlyReport.reduce((sum, m) => sum + (m.TotalPaymentsCollected || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
                      <p className="text-sm text-gray-600 mb-1">Outstanding</p>
                      <p className="text-2xl font-bold text-red-600">
                        ${monthlyReport.reduce((sum, m) => sum + (m.OutstandingBalance || 0), 0).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Monthly Table */}
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Enrollments</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Families</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Children</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discounts</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Collected</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {monthlyReport.map((month, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap font-medium">
                                {month.MonthName} {month.ChargeYear}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {month.ActiveEnrollmentsCount || 0}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{month.TotalFamilies || 0}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{month.TotalChildren || 0}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                                ${(month.TotalRevenue || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-orange-600">
                                ${(month.TotalDiscounts || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-green-600">
                                ${(month.NetRevenue || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-purple-600">
                                ${(month.TotalPaymentsCollected || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right">
                                <span className={`text-sm font-semibold ${
                                  (month.OutstandingBalance || 0) > 0 ? 'text-red-600' : 'text-green-600'
                                }`}>
                                  ${(month.OutstandingBalance || 0).toFixed(2)}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-xs">
                                <div className="flex flex-col space-y-1">
                                  <span className="text-green-600">✓ {month.PaidCount || 0}</span>
                                  <span className="text-yellow-600">◐ {month.PartialCount || 0}</span>
                                  <span className="text-blue-600">● {month.BilledCount || 0}</span>
                                  <span className="text-gray-500">✗ {month.CancelledCount || 0}</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-100 font-bold">
                          <tr>
                            <td className="px-4 py-3">TOTAL</td>
                            <td className="px-4 py-3 text-center">-</td>
                            <td className="px-4 py-3 text-center">-</td>
                            <td className="px-4 py-3 text-center">-</td>
                            <td className="px-4 py-3 text-right">
                              ${monthlyReport.reduce((sum, m) => sum + (m.TotalRevenue || 0), 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right text-orange-600">
                              ${monthlyReport.reduce((sum, m) => sum + (m.TotalDiscounts || 0), 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right text-green-600">
                              ${monthlyReport.reduce((sum, m) => sum + (m.NetRevenue || 0), 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right text-purple-600">
                              ${monthlyReport.reduce((sum, m) => sum + (m.TotalPaymentsCollected || 0), 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right text-red-600">
                              ${monthlyReport.reduce((sum, m) => sum + (m.OutstandingBalance || 0), 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-3"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ✅ NEW: CHILDREN DETAILED REPORT TAB */}
          {activeTab === 'children-report' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Children Detailed Report</h2>
                <button
                  onClick={() => {
                    const csv = [
                      ['Child Name', 'Family ID', 'Family Name', 'Status', 'Package', 'Monthly Rate', 'Enrollment Discount', 
                       'Total Charges', 'Total Discounts', 'Net Amount', 'Total Payments', 'Balance', 
                       'Months Charged', 'Paid', 'Partial', 'Billed', 'Cancelled'],
                      ...childrenDetailedReport.map(child => [
                        child.ChildName,
                        child.FamilyID,
                        child.FamilyName,
                        child.EnrollmentStatus,
                        child.PackageName,
                        child.MonthlyRate.toFixed(2),
                        child.EnrollmentDiscount.toFixed(2),
                        child.TotalCharges.toFixed(2),
                        child.TotalDiscounts.toFixed(2),
                        child.NetAmount.toFixed(2),
                        child.TotalPayments.toFixed(2),
                        child.RemainingBalance.toFixed(2),
                        child.MonthsCharged,
                        child.PaidCharges,
                        child.PartialCharges,
                        child.BilledCharges,
                        child.CancelledCharges
                      ])
                    ];
                    const csvContent = csv.map(row => row.join(',')).join('\n');
                    const blob = new Blob([csvContent], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `daycare-children-report-${selectedYear}.csv`;
                    a.click();
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  📥 Export to CSV
                </button>
              </div>

              {childrenDetailedReport.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No children data available for {selectedYear}
                </div>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
                      <p className="text-sm text-gray-600 mb-1">Total Children</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {childrenDetailedReport.length}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
                      <p className="text-sm text-gray-600 mb-1">Total Charges</p>
                      <p className="text-2xl font-bold text-green-600">
                        ${childrenDetailedReport.reduce((sum, c) => sum + (c.TotalCharges || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
                      <p className="text-sm text-gray-600 mb-1">Total Discounts</p>
                      <p className="text-2xl font-bold text-orange-600">
                        ${childrenDetailedReport.reduce((sum, c) => sum + (c.TotalDiscounts || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
                      <p className="text-sm text-gray-600 mb-1">Total Payments</p>
                      <p className="text-2xl font-bold text-purple-600">
                        ${childrenDetailedReport.reduce((sum, c) => sum + (c.TotalPayments || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
                      <p className="text-sm text-gray-600 mb-1">Total Balance</p>
                      <p className="text-2xl font-bold text-red-600">
                        ${childrenDetailedReport.reduce((sum, c) => sum + (c.RemainingBalance || 0), 0).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Children Table */}
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Child</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Family</th>
                            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Package</th>
                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Charges</th>
                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discounts</th>
                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net</th>
                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Months</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {childrenDetailedReport.map((child, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{child.ChildName}</div>
                                <div className="text-xs text-gray-500">{child.ChildID}</div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{child.FamilyName}</div>
                                <div className="text-xs text-gray-500">{child.FamilyID}</div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-center">
                                <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(child.EnrollmentStatus)}`}>
                                  {child.EnrollmentStatus === 'Active' && '✅'}
                                  {child.EnrollmentStatus === 'Frozen' && '🧊'}
                                  {child.EnrollmentStatus === 'Cancelled' && '❌'}
                                  {child.EnrollmentStatus === 'Enrolled at School' && '🏫'}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-sm text-gray-900">{child.PackageName}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-right text-sm">
                                ${(child.MonthlyRate || 0).toFixed(2)}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right text-sm">
                                ${(child.TotalCharges || 0).toFixed(2)}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right text-sm text-orange-600">
                                -${(child.TotalDiscounts || 0).toFixed(2)}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-semibold">
                                ${(child.NetAmount || 0).toFixed(2)}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right text-sm text-green-600">
                                ${(child.TotalPayments || 0).toFixed(2)}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right">
                                <span className={`text-sm font-semibold ${
                                  (child.RemainingBalance || 0) > 0 ? 'text-red-600' : 'text-green-600'
                                }`}>
                                  ${(child.RemainingBalance || 0).toFixed(2)}
                                </span>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-center">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  {child.MonthsCharged || 0}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-100 font-bold">
                          <tr>
                            <td colSpan="5" className="px-3 py-3 text-right">TOTAL</td>
                            <td className="px-3 py-3 text-right">
                              ${childrenDetailedReport.reduce((sum, c) => sum + (c.TotalCharges || 0), 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-3 text-right text-orange-600">
                              -${childrenDetailedReport.reduce((sum, c) => sum + (c.TotalDiscounts || 0), 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-3 text-right">
                              ${childrenDetailedReport.reduce((sum, c) => sum + (c.NetAmount || 0), 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-3 text-right text-green-600">
                              ${childrenDetailedReport.reduce((sum, c) => sum + (c.TotalPayments || 0), 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-3 text-right text-red-600">
                              ${childrenDetailedReport.reduce((sum, c) => sum + (c.RemainingBalance || 0), 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-3"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showChildModal && (
        <DaycareChildrenModal
          isOpen={showChildModal}
          onClose={() => setShowChildModal(false)}
          onSuccess={() => {
            setShowChildModal(false);
            loadData();
            showSuccess('Child registered successfully!');
          }}
          families={families}
          apiCall={apiCall}
          showError={showError}
        />
      )}

      {showEnrollModal && (
        <DaycareEnrollmentsModal
          isOpen={showEnrollModal}
          onClose={() => setShowEnrollModal(false)}
          onSuccess={() => {
            loadData();
            setShowEnrollModal(false);
          }}
          children={children.filter(c => !c.CurrentEnrollmentStatus)}
          packages={packages}
          academicYear={enrollmentYear}
          apiCall={apiCall}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}

      {showBillingModal && (
        <DaycareBillingModal
          isOpen={showBillingModal}
          onClose={() => setShowBillingModal(false)}
          onSuccess={() => {
            setShowBillingModal(false);
            loadData();
          }}
          academicYear={selectedYear}
          apiCall={apiCall}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}

      {showPackageModal && (
        <DaycarePackagesModal
          isOpen={showPackageModal}
          onClose={() => {
            setShowPackageModal(false);
            setSelectedPackage(null);
          }}
          onSuccess={() => {
            setShowPackageModal(false);
            setSelectedPackage(null);
            loadData();
            showSuccess(selectedPackage ? 'Package updated!' : 'Package created!');
          }}
          packageData={selectedPackage}
          apiCall={apiCall}
          showError={showError}
        />
      )}

      {/* Child Transaction History Modal */}
      {showTransactionModal && selectedChild && (
        <DaycareChildTransactionHistoryModal
          isOpen={showTransactionModal}
          onClose={() => {
            setShowTransactionModal(false);
            setSelectedChild(null);
          }}
          child={selectedChild}
          apiCall={apiCall}
          selectedYear={selectedYear}
        />
      )}

      {/* Transfer Package Modal */}
      <DaycareTransferPackageModal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setSelectedEnrollment(null);
        }}
        onSuccess={() => {
          loadData();
          setShowTransferModal(false);
          setSelectedEnrollment(null);
        }}
        enrollment={selectedEnrollment}
        isGeneralTransfer={true}
        apiCall={apiCall}
        showError={showError}
        showSuccess={showSuccess}
      />

      {/* Move to School Modal */}
      {showMoveModal && moveEnrollment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center gap-2 px-5 py-4 border-b">
              <GraduationCap className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-gray-900">Move to School</h3>
            </div>
            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-gray-700">
                End the daycare enrollment for{' '}
                <span className="font-medium">{moveEnrollment.FirstName} {moveEnrollment.LastName}</span>{' '}
                and mark it <span className="font-medium text-indigo-700">Enrolled at School</span>.
                Future daycare charges stop; any unpaid balance is kept.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link to school student <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <select
                  value={moveStudentId}
                  onChange={(e) => setMoveStudentId(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">— Don't link now (auto-match by name) —</option>
                  {moveStudents.map((s) => (
                    <option key={s.StudentID} value={s.StudentID}>
                      {s.FullName || `${s.FirstName || ''} ${s.LastName || ''}`.trim()} ({s.StudentID})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Links this child to their school record so the family statement can show
                  “from daycare”. If the student isn’t registered yet, leave this blank — it
                  links automatically when you enrol them, or you can move them again later.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t">
              <button
                onClick={() => { setShowMoveModal(false); setMoveEnrollment(null); }}
                disabled={moveSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submitMoveToSchool}
                disabled={moveSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {moveSubmitting ? 'Moving…' : 'Move to School'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Freeze Modal */}
      <DaycareFreezeModal
        isOpen={showFreezeModal}
        onClose={() => {
          setShowFreezeModal(false);
          setSelectedEnrollment(null);
        }}
        onSuccess={() => {
          loadData();
          setShowFreezeModal(false);
          setSelectedEnrollment(null);
        }}
        enrollment={selectedEnrollment}
        apiCall={apiCall}
        showError={showError}
        showSuccess={showSuccess}
      />

      {/* Unfreeze Modal */}
	<DaycareUnfreezeModal
  	isOpen={showUnfreezeModal}
  	onClose={() => {
   	 setShowUnfreezeModal(false);
    	setSelectedEnrollment(null);
  	}}
  	onSuccess={() => {
    	loadData();
    	setShowUnfreezeModal(false);
    	setSelectedEnrollment(null);
  	}}
  	enrollment={selectedEnrollment}
  	apiCall={apiCall}
  	showError={showError}
  	showSuccess={showSuccess}
	/>

      {/* Cancel Modal */}
      <DaycareCancelModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setSelectedEnrollment(null);
        }}
        onSuccess={() => {
          loadData();
          setShowCancelModal(false);
          setSelectedEnrollment(null);
        }}
        enrollment={selectedEnrollment}
        apiCall={apiCall}
        showError={showError}
        showSuccess={showSuccess}
      />

      {/* Additional Charges Modal */}
      {showAdditionalChargesModal && (
        <DaycareAdditionalChargesModal
          isOpen={showAdditionalChargesModal}
          onClose={() => {
            setShowAdditionalChargesModal(false);
            setSelectedEnrollment(null);
          }}
          enrollment={selectedEnrollment}
          apiCall={apiCall}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}

      {/* Print Report Modal */}
      {showPrintModal && (
        <DaycarePrintReportModal
          monthlyReport={monthlyReport}
          selectedYear={selectedYear}
          onClose={() => setShowPrintModal(false)}
        />
      )}
    </div>
  );
};

export default Daycare;